import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization, CoreCustomer } from '@/lib/zifekCore';
import { listOrganizationCustomers } from '@/lib/zifekCore';
import {
  CUSTOMER_RELATION_CHAIN,
  resolveCustomerContext,
  type CustomerGlobalContext,
} from '@/lib/coreCustomerContext';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function CustomerContextTab({ commerceId, org }: Props) {
  const [customers, setCustomers] = useState<CoreCustomer[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [context, setContext] = useState<CustomerGlobalContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listOrganizationCustomers(org.id);
      setCustomers(list);
      if (list.length > 0) setSelectedId(list[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedId == null) { setContext(null); return; }
    let active = true;
    setLoadingCtx(true);
    resolveCustomerContext({ organizationId: org.id, commerceId, customerId: selectedId })
      .then((ctx) => { if (active) setContext(ctx); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Contexte indisponible.'); })
      .finally(() => { if (active) setLoadingCtx(false); });
    return () => { active = false; };
  }, [selectedId, org.id, commerceId]);

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Relation globale.</span> Un client n’est pas dupliqué :
        le socle relie son profil à ses rendez-vous, réservations, commandes, factures, paiements, avis, favoris,
        documents et conversations. Zifek AI comprend ainsi le contexte complet sans recopier les informations.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Chaîne relationnelle */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {CUSTOMER_RELATION_CHAIN.map((step, i, arr) => (
            <span key={step.key} className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap flex items-center gap-1.5 ${i === 0 ? 'bg-background-100 text-foreground-700' : i === 1 ? 'bg-primary-100 text-primary-900' : 'bg-secondary-100 text-secondary-900'}`}>
                <i className={step.icon}></i>{step.label}
              </span>
              {i < arr.length - 1 && <i className="ri-arrow-down-line text-foreground-300"></i>}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : customers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <i className="ri-user-search-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600 mt-2">Aucune fiche client pour l’instant.</p>
          <p className="text-xs text-foreground-400 mt-1">Les clients sont créés automatiquement depuis les commandes, rendez-vous et le carnet de crédit.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <label className="block flex-1">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Client</span>
              <select className={INPUT} value={selectedId ?? ''} onChange={(e) => setSelectedId(Number(e.target.value))}>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name || c.email || `Client #${c.id}`}</option>
                ))}
              </select>
            </label>
            {context && (
              <div className="flex gap-3">
                <div className="rounded-lg border bg-background-50 border-background-200/70 px-4 py-2 text-center">
                  <p className="text-lg font-bold text-foreground-900">{context.totals.relations}</p>
                  <p className="text-[11px] text-foreground-500">Relations</p>
                </div>
                <div className="rounded-lg border bg-background-50 border-background-200/70 px-4 py-2 text-center">
                  <p className="text-lg font-bold text-foreground-900">{context.totals.categories}</p>
                  <p className="text-[11px] text-foreground-500">Catégories</p>
                </div>
              </div>
            )}
          </div>

          {loadingCtx ? (
            <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
          ) : context?.customer ? (
            <>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0"><i className="ri-user-star-line text-lg"></i></div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-900">{context.customer.displayName || 'Client'}</p>
                  <p className="text-[11px] text-foreground-400 truncate">
                    {[context.customer.email, context.customer.phone, context.customer.companyName].filter(Boolean).join(' · ') || '—'}
                  </p>
                </div>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">{context.customer.customerType || 'client'}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {context.groups.map((g) => (
                  <div key={g.key} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                    <div className="px-3 py-2.5 border-b border-background-200/60 flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center"><i className={g.icon}></i></span>
                      <span className="text-xs font-semibold text-foreground-800">{g.label}</span>
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${g.items.length > 0 ? 'bg-primary-100 text-primary-900' : 'bg-background-100 text-foreground-400'}`}>{g.items.length}</span>
                    </div>
                    {g.items.length === 0 ? (
                      <p className="px-3 py-4 text-[11px] text-foreground-400 text-center">Aucune relation</p>
                    ) : (
                      <ul className="divide-y divide-background-200/60 max-h-52 overflow-y-auto">
                        {g.items.map((it) => (
                          <li key={`${g.key}-${it.id}`} className="px-3 py-2">
                            <p className="text-[12px] font-medium text-foreground-800 truncate">{it.title}</p>
                            <p className="text-[10px] text-foreground-400 truncate">
                              {[it.subtitle, it.status, it.date ? new Date(it.date).toLocaleDateString('fr-FR') : null].filter(Boolean).join(' · ') || '—'}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs text-foreground-400 text-center py-8">Sélectionnez un client pour afficher son contexte global.</p>
          )}
        </>
      )}
    </div>
  );
}